import {
  filterDOMProps,
  useLayoutEffect,
  useObjectRef,
  useResizeObserver,
  useValueEffect,
} from '@react-aria/utils';
import type { DOMProps } from '@react-types/shared';
import type { ReactElement, ReactNode } from 'react';
import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useRef,
} from 'react';
import {
  type ContextValue,
  Provider,
  type SlotProps,
} from 'react-aria-components';

import type { UnsafeStyleProps } from '@infinex/ui-styles';
import { cn, useContextProps } from '@infinex/ui-styles';

import { ButtonContext } from './Button';
import { LinkButtonContext } from './LinkButton';
import type { ButtonProps } from './types';

export type ButtonGroupProps = {
  /** The buttons contained within the group. */
  children: ReactNode;
  /**
   * The axis the group should align with. Setting this to 'vertical' will prevent
   * any switching behaviors between 'vertical' and 'horizontal'.
   *
   * @default horizontal
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * The alignment of buttons within the group.
   *
   * @default start
   */
  align?: 'start' | 'end' | 'center' | 'justify';
  /**
   * The size of each button within the group.
   * @default medium
   */
  size?: ButtonProps['size'];
  /**
   * The static color of each button within the group.
   */
  staticColor?: ButtonProps['staticColor'];
  /** Whether all buttons in the group are disabled. */
  isDisabled?: boolean;
  /**
   * Wrap the element with some custom behaviour, within a slotted component.
   */
  render?: (element: ReactElement) => ReactNode;
} & DOMProps &
  UnsafeStyleProps &
  SlotProps;

type ButtonGroupContextValue = {
  /** Whether the ButtonGroup shouldn't be rendered. */
  isHidden?: boolean;
} & Partial<ButtonGroupProps>;

export const ButtonGroupContext = createContext<
  ContextValue<Partial<ButtonGroupContextValue>, HTMLDivElement>
>({});

/**
 * Display multiple related actions stacked or in a row. The button group
 * controls overflow, arrangement, and spacing.
 *
 * [Taurus docs: `ButtonGroup`](https://ui-sb.app.infinex.dev/?path=/docs/components-buttongroup--docs)
 */
export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
  function ButtonGroup(props, ref) {
    [props, ref] = useContextProps(props, ref, ButtonGroupContext);
    const {
      align = 'start',
      children,
      isDisabled,
      orientation = 'horizontal',
      size = 'medium',
      staticColor,
      render,
      ...otherProps
    } = props;

    const domRef = useObjectRef(ref);
    const [hasOverflow, setHasOverflow] = useValueEffect(false);
    const checkForOverflow = useCallback(() => {
      const computeHasOverflow = () => {
        if (domRef.current && orientation === 'horizontal') {
          const buttonGroupChildren = Array.from(
            domRef.current.children
          ) as HTMLElement[];
          // + 1 to account for rounding errors
          const maxX = domRef.current.offsetWidth + 1;
          // If any buttons have negative X positions (align="end") or extend beyond
          // the width of the button group (align="start"), then switch to vertical.
          if (
            buttonGroupChildren.some(
              (child) =>
                child.offsetLeft < 0 ||
                child.offsetLeft + child.offsetWidth > maxX
            )
          ) {
            return true;
          }
          return false;
        }
      };
      if (orientation === 'horizontal') {
        setHasOverflow(function* () {
          // Force to horizontal for measurement.
          yield false;

          // Measure, and update if there is overflow.
          yield computeHasOverflow();
        });
      }
      // Check for overflow when`children` change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domRef, orientation, setHasOverflow, children]);

    // There are two main reasons we need to remeasure:
    // 1. Internal changes: Check for initial overflow or when
    //    orientation/scale/children change (from checkForOverflow dep array)
    useLayoutEffect(() => {
      checkForOverflow();
    }, [checkForOverflow]);

    // 2. External changes: buttongroup won't change size due to any parents
    //    changing size, so listen to its container for size changes to figure
    //    out if we should remeasure
    const parent = useRef<HTMLElement | null>(null);
    useLayoutEffect(() => {
      if (domRef.current) {
        parent.current = domRef.current.parentElement;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [domRef.current]);
    useResizeObserver({
      ref: parent,
      onResize: checkForOverflow,
      box: 'border-box',
    });

    const prevButtonContext = useContext(ButtonContext);
    const prevLinkButtonContext = useContext(LinkButtonContext);

    if ((props as ButtonGroupContextValue).isHidden) {
      return null;
    }

    // make conditional styles more terse and easier to read
    const isHorizontal = orientation === 'horizontal';
    const isVertical = orientation === 'vertical' || hasOverflow;

    const groupElement = (
      <div
        {...filterDOMProps(otherProps)}
        ref={domRef}
        style={props.style}
        className={cn(
          'relative inline-flex w-full min-w-0 items-start',
          '*:shrink-0',
          {
            'gap-4': size === 'large',
            'gap-3': size === 'medium',
            'gap-2': size === 'small',
            'gap-1': size === 'tiny',
            // orientation
            'flex-col': isVertical,
            'flex-row': isHorizontal && !hasOverflow,
            // align horizontal
            'justify-start': isHorizontal && align === 'start',
            'justify-end': isHorizontal && align === 'end',
            'justify-center': isHorizontal && align === 'center',
            '*:min-w-min *:flex-1': isHorizontal && align === 'justify',
            // align vertical
            'items-start': isVertical && align === 'start',
            'items-end': isVertical && align === 'end',
            'items-center': isVertical && align === 'center',
            'items-stretch': isVertical && align === 'justify',
          },
          props.className
        )}
      >
        <Provider
          values={[
            [
              ButtonContext,
              { ...prevButtonContext, isDisabled, size, staticColor },
            ],
            [
              LinkButtonContext,
              { ...prevLinkButtonContext, isDisabled, size, staticColor },
            ],
          ]}
        >
          {children}
        </Provider>
      </div>
    );

    return render ? render(groupElement) : groupElement;
  }
);
